'use client';

import { useState } from 'react';

import {
  InvitationsTable,
  PossibleWorkspacesTable,
  ArchivedWorkspacesTable,
  SentJoinRequestsTable,
  SuccessStep,
  CreateWorkspaceButton,
} from '@/features/workspaces/components';
import { LogoutButton } from '@/ui';
import { Stepper } from '@web/ui';

import type {
  WorkspaceInviteItem,
  GetUserInvitesResponse,
  JoinRequestItem,
  ArchivedWorkspaceItem,
} from '@/features/workspaces/types';
import type { Workspace } from '@web/types';

type AcceptedWorkspace = Pick<Workspace, 'name' | 'slug'>;

type Props = {
  invitations: WorkspaceInviteItem[];
  possibleWorkspaces: GetUserInvitesResponse['possible_workspaces'];
  joinRequests?: JoinRequestItem[];
  archivedWorkspaces?: ArchivedWorkspaceItem[];
};

export function WorkspaceSelectSteps({
  invitations,
  possibleWorkspaces,
  joinRequests = [],
  archivedWorkspaces = [],
}: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [joinedWorkspace, setJoinedWorkspace] =
    useState<AcceptedWorkspace | null>(null);

  const handleNextStep = () => {
    setActiveStep((prev) => (prev + 1) % steps.length);
  };

  const isPossibleWorkspaces = possibleWorkspaces.length > 0;
  const isJoinRequests = joinRequests.length > 0;
  const reactivatableArchivedWorkspaces = archivedWorkspaces.filter(
    (workspace) => workspace.can_reactivate,
  );
  const isArchivedWorkspaces = reactivatableArchivedWorkspaces.length > 0;

  const steps = [
    {
      label: 'Workspaces',
      content: (
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <InvitationsTable
            items={invitations}
            onAccepted={(workspace) => {
              setJoinedWorkspace(workspace);
              handleNextStep();
            }}
          />

          {isArchivedWorkspaces && (
            <>
              <br />
              <ArchivedWorkspacesTable
                items={reactivatableArchivedWorkspaces}
                canReactivate
                onReactivated={(workspace) => {
                  setJoinedWorkspace(workspace);
                  handleNextStep();
                }}
              />
            </>
          )}

          {isPossibleWorkspaces && (
            <>
              <br />
              <div style={{ width: '100%', minWidth: 650 }}>
                <PossibleWorkspacesTable items={possibleWorkspaces} />
              </div>
            </>
          )}

          {isJoinRequests && (
            <div style={{ marginTop: 16 }}>
              <SentJoinRequestsTable items={joinRequests} />
            </div>
          )}
        </div>
      ),
    },
    {
      label: 'Success',
      content: <SuccessStep workspace={joinedWorkspace} />,
    },
  ];

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 960,
        margin: '0 auto',
      }}
    >
      <Stepper
        steps={steps}
        activeStep={activeStep}
        onStepChange={setActiveStep}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <CreateWorkspaceButton />

        <LogoutButton variant="buttonStyle" />
      </div>
    </div>
  );
}
